import React from 'react';
import styled, { withTheme } from 'styled-components';
import { getFirstValue, getPreviewImage, toArray } from './utils';
import { useSelector } from 'react-redux';

const Section = styled.section`
  display: grid;
  gap: 12px;
`;

const SectionLabel = styled.div`
  color: var(--pm-text-muted, rgba(71, 85, 105, 0.82));
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CardsList = styled.div`
  display: grid;
  gap: 14px;
`;

const Card = styled.button`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 138px minmax(0, 1fr);
  align-items: stretch;
  overflow: hidden;
  padding: 0;
  border-radius: 12px;
  border: 1px solid var(--pm-stage-border, rgba(148, 163, 184, 0.18));
  background: var(--pm-card-bg, rgba(255, 255, 255, 0.82));
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: var(--pm-accent-border, rgba(37, 99, 235, 0.18));
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.09);
  }

  @media (max-width: 420px) {
    grid-template-columns: 118px minmax(0, 1fr);
  }
`;

const ThumbnailWrap = styled.div`
  position: relative;
  min-height: 94px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0.12));
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 94px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  text-align: center;
  color: var(--pm-text-faint, rgba(100, 116, 139, 0.82));
  font-size: 12px;
  line-height: 1.45;
  background: var(--pm-placeholder-bg, rgba(248, 250, 252, 0.92));
`;

const Content = styled.div`
  min-width: 0;
  display: grid;
  gap: 8px;
  padding: 12px 14px;
`;

const CardTitle = styled.div`
  padding-right: 28px;
  color: var(--pm-text-primary, #0f172a);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  word-break: break-word;
`;

const CreatorText = styled.div`
  color: var(--pm-text-muted, rgba(71, 85, 105, 0.9));
  font-size: 12px;
  line-height: 1.3;
`;

const MetaList = styled.div`
  display: grid;
  gap: 8px;
`;

const MetaRow = styled.div`
  display: grid;
  gap: 4px;
`;

const CategoryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const CategoryPill = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--pm-accent-soft, rgba(37, 99, 235, 0.1));
  color: var(--pm-accent-text, #2563eb);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
`;

const SubcategoryText = styled.div`
  color: var(--pm-text-muted, rgba(100, 116, 139, 0.92));
  font-size: 12px;
  line-height: 1.3;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
`;

const FormatTag = styled.div`
  margin: 0;
  padding: 0.1rem 0.4rem;
  line-height: 1.1;
  border-radius: 999px;
  background: var(--pm-chip-bg, rgba(241, 245, 249, 0.95));
  color: var(--pm-chip-text, #475569);
`;

const ActionIconButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  color: var(--pm-text-muted, rgba(71, 85, 105, 0.82));
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.92);
    color: var(--pm-text-primary, #0f172a);
  }
`;

const EmptyState = styled.div`
  border-radius: 16px;
  border: 1px dashed var(--pm-placeholder-border, rgba(148, 163, 184, 0.28));
  padding: 16px;
  color: var(--pm-text-faint, rgba(100, 116, 139, 0.82));
  font-size: 14px;
  line-height: 1.6;
  background: var(--pm-placeholder-bg, rgba(248, 250, 252, 0.92));
`;

const SuggestedModelsSection = withTheme(props => {
  const {
    models = [],
    title = 'Suggested 3D Models',
    maxItems = 4,
    emptyText = 'Suggested models will appear here once related content is available.',
    onModelClick,
    onActionClick,
    hideActionButton = false,
    theme,
  } = props;

  const visibleModels = toArray(models).filter(Boolean).slice(0, maxItems);
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;

  const handleCardClick = model => {
    onModelClick?.(model);
  };

  const handleActionClick = (event, model) => {
    event.stopPropagation();
    onActionClick?.(model);
  };

  return (
    <Section>
      <SectionLabel>{title}</SectionLabel>

      {!visibleModels.length ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : (
        <CardsList>
          {visibleModels.map(model => {
            const titleValue = getFirstValue(model?.title, model?.name, 'Untitled model');
            const creatorValue = model?.creator || 'ArchVision';
            const categoryValue = model?.category || null;
            const subcategoryValue = model?.subcategory || null;
            const tags = toArray(model?.tags || []).filter(Boolean);

            const modelRpcGuid = model.rpc_guid;
            const imageSrc = getPreviewImage(model, modelRpcGuid);

            return (
              <Card
                key={modelRpcGuid || model?.id || `${titleValue}-${creatorValue}`}
                type="button"
                onClick={() => handleCardClick(model)}
              >
                <ThumbnailWrap>
                  {imageSrc ? (
                    <Thumbnail src={imageSrc} alt={titleValue} loading="lazy" />
                  ) : (
                    <ThumbnailPlaceholder>No preview image</ThumbnailPlaceholder>
                  )}
                </ThumbnailWrap>

                <Content>
                  <CardTitle>{titleValue}</CardTitle>
                   {creatorValue && <CreatorText>{creatorValue}</CreatorText> }

                  <MetaList>
                    {(categoryValue || subcategoryValue) && (
                      <MetaRow>
                        <CategoryRow>
                          {categoryValue && <CategoryPill>{categoryValue}</CategoryPill>}
                          {subcategoryValue && (
                            <SubcategoryText>{subcategoryValue}</SubcategoryText>
                          )}
                        </CategoryRow>
                      </MetaRow>
                    )}

                    {!!tags.length && (
                      <MetaRow>
                        <TagList>
                          {tags.slice(0, 4).map(tag => (
                            <FormatTag
                              key={tag}
                              theme={theme}
                              $selectedTheme={selectedTheme}
                              fontSize={10}
                            >
                              {tag}
                            </FormatTag>
                          ))}
                        </TagList>
                      </MetaRow>
                    )}
                  </MetaList>
                </Content>

                {!hideActionButton && (
                  <ActionIconButton
                    type="button"
                    aria-label={`Open ${titleValue}`}
                    onClick={event => handleActionClick(event, model)}
                  />
                )}
              </Card>
            );
          })}
        </CardsList>
      )}
    </Section>
  );
});

export default SuggestedModelsSection;